import {
  EtapaProjeto,
  indexOfEtapa,
  ProjetoType,
  StatusEtapa,
} from 'models/projeto';
import { useCallback, useMemo } from 'react';
import { etapasConfig } from 'utils/projeto';
import {
  DEFAULT_VALID,
  DEFAULT_VALIDATION_MODEL,
  hasAnyValue,
  ValidationType,
} from 'utils/ValidationUtils';

export type ProjetosValidatorType = {
  etapaIsDisabled: { [k in EtapaProjeto]: boolean };
  etapaIsCompleted: { [k in EtapaProjeto]: boolean };
  fieldIsValid: { [k in keyof ProjetoType]: ValidationType };
  getCampoIsValid: (options: {
    key: keyof ProjetoType;
    etapa: EtapaProjeto;
    helperText?: string;
  }) => ValidationType;
};

export default function ProjetosValidator(projeto: Partial<ProjetoType>) {
  const validator: Partial<ProjetosValidatorType> = {};

  validator.getCampoIsValid = useCallback(
    (options: {
      key: keyof ProjetoType;
      etapa: EtapaProjeto;
      helperText?: string;
    }) => {
      if (
        etapasConfig[options.etapa].fields[options.key]?.required &&
        !hasAnyValue(projeto[options.key])
      ) {
        return DEFAULT_VALIDATION_MODEL(true, options.helperText);
      }

      const validation =
        etapasConfig[options.etapa].fields[options.key]?.validation;

      return typeof validation === 'function'
        ? validation(projeto[options.key] as any, projeto)
        : DEFAULT_VALID;
    },
    [projeto]
  );

  validator.etapaIsCompleted = useMemo(() => {
    return Object.values(EtapaProjeto).reduce((resultado, etapa) => {
      let valid = true;
      switch (etapa) {
        case EtapaProjeto.infosIniciais:
        default:
          valid =
            !Object.values(etapasConfig[etapa].fields).length ||
            Object.keys(etapasConfig[etapa].fields).every((key) => {
              return typeof validator.getCampoIsValid === 'function'
                ? !validator.getCampoIsValid({
                    key: key as keyof ProjetoType,
                    etapa,
                  }).error
                : true;
            });
      }
      return {
        ...resultado,
        [etapa]: valid,
      };
    }, {} as { [k in EtapaProjeto]: boolean });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeto, validator.getCampoIsValid]);

  validator.etapaIsDisabled = useMemo(() => {
    return Object.values(EtapaProjeto).reduce((resultado, etapa) => {
      let disabled = false;
      switch (etapa) {
        case EtapaProjeto.infosIniciais:
          disabled = false;
          break;
        default:
          if (
            validator &&
            validator.etapaIsCompleted &&
            (!validator.etapaIsCompleted[EtapaProjeto.infosIniciais] ||
              !projeto.avaliacoes?.some((avaliacao) => {
                return (
                  avaliacao.etapa ===
                    indexOfEtapa(EtapaProjeto.infosIniciais) &&
                  avaliacao.status === StatusEtapa.Aprovada
                );
              }))
          ) {
            disabled = true;
          }
      }
      return {
        ...resultado,
        [etapa]: disabled,
      };
    }, {} as { [k in EtapaProjeto]: boolean });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validator.etapaIsCompleted]);

  return validator as ProjetosValidatorType;
}
